<i18n>
{
  "fr": {
    "configuratorLayerEditorModalEdit": {
      "actions": {
        "updateImage": "Changer d'image",
        "text-next": "Valider le texte",
        "ai-editing": "Modifier avec IA",
        "image-next": "Valider l'image",
        "removeText": "Annuler"
      }
    }
  }
}
</i18n>

<template>
  <form class="configurator-layer-editor-modal-edit" @submit="onSubmit">
    <div
      v-if="customization.type === ZoneType.TEXT"
      class="configurator-layer-editor-modal-edit__sub-header"
    >
      <ConfiguratorLayerEditorModalTextToolbar
        ref="textToolbar"
        :typo-list="sortedTypoList"
        v-bind.sync="textCustomization"
        :color-picker="!color"
      />
    </div>

    <div class="configurator-layer-editor-modal-edit__body">
      <ConfiguratorLayerEditorStage
        v-model="customizationCustomConfig"
        class="configurator-layer-editor-modal-edit__stage"
        v-bind="editorStageProps"
        @focus-request="focusTextInput"
      />
    </div>

    <div class="configurator-layer-editor-modal-edit__actions">
      <div class="configurator-layer-editor-modal-edit__actions-container">
        <BtnToolbar
          centered
          class="configurator-layer-editor-modal-edit__actions-toolbar"
        >
          <BaseBtn
            v-if="zone.type === ZoneType.TEXT"
            border
            @click="resetText"
            v-text="$t('configuratorLayerEditorModalEdit.actions.removeText')"
          />

          <BaseBtn
            v-else-if="zone.type === ZoneType.IMAGE"
            border
            @click="goBack"
            v-text="$t('configuratorLayerEditorModalEdit.actions.updateImage')"
          />

          <BaseBtn
            v-if="zone.type === ZoneType.IMAGE && isDebugAiGenEnabled"
            border
            @click="goToAiEditing"
            v-text="$t('configuratorLayerEditorModalEdit.actions.ai-editing')"
          />

          <BaseBtn
            class="configurator-layer-editor-modal-edit__btn-next"
            secondary
            type="submit"
            :disabled="!isFormValid"
            v-text="
              zone.type === ZoneType.TEXT
                ? $t('configuratorLayerEditorModalEdit.actions.text-next')
                : $t('configuratorLayerEditorModalEdit.actions.image-next')
            "
          />
        </BtnToolbar>
      </div>
    </div>
  </form>
</template>

<script>
import BtnToolbar from '../../../BtnToolbar';
import BaseBtn from '../../../BaseBtn';
import ConfiguratorLayerEditorStage from '../../ConfiguratorLayerEditorStage';
import {
  ConfiguratorGetters,
  mapGetters as mapConfiguratorGetters,
} from '../../../../store/modules/configurator';
import { filterProps } from '../../../../utils/ObjectUtils';
import { ZoneCustomizationCustomConfigProps } from '../../../../utils/StageUtils';
import { ZoneType } from '../../../../api/static/zones';
import ConfiguratorLayerEditorModalTextToolbar from './ConfiguratorLayerEditorModalTextToolbar';
import {
  GlassesGetter,
  mapGetters as mapGlassesGetters,
  prefixType as prefixGlassesType,
} from '../../../../store/modules/configurator/glasses';
import { getTypoName } from '../../../../utils/TypoUtils';

export default {
  name: 'ConfiguratorLayerEditorModalEdit',
  emits: ['go-to-ai-editing'],
  components: {
    ConfiguratorLayerEditorModalTextToolbar,
    ConfiguratorLayerEditorStage,
    BaseBtn,
    BtnToolbar,
  },
  props: {
    group: {
      type: Object,
      required: true,
    },
    customization: {
      type: Object,
      required: true,
    },
    color: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      /**
       * @type {ZoneCustomizationCustomConfig|undefined}
       */
      customizationCustomConfig: undefined,
      textCustomization: {
        text: undefined,
        bold: false,
        underlined: false,
        italic: false,
        typoId: undefined,
        color: undefined,
      },
      ZoneType,
    };
  },
  computed: {
    ...mapConfiguratorGetters({
      activeSize: ConfiguratorGetters.GET_ACTIVE_SIZE,
      activePrintingArea: ConfiguratorGetters.GET_ACTIVE_PRINTING_AREA,
    }),
    ...mapGlassesGetters({
      typoList: GlassesGetter.GET_TYPOS,
    }),
    sortedTypoList() {
      return this.typoList.slice().sort((a, b) => a.name.localeCompare(b.name));
    },
    readOnlyCustomizations() {
      return this.group.customizations.filter(
        z => z.id !== this.customization.id
      );
    },
    editorStageProps() {
      /** @type {SizeFull} */
      const activeSize = this.activeSize;
      /** @type {PrintingArea} */
      const activePrintingArea = this.activePrintingArea;

      const props = {
        shapeWidth: activeSize.diameter,
        shapeHeight: activeSize.height,
        shapeUrl: activeSize.shapeUrl,
        printingArea: {
          x: (activeSize.diameter * Math.PI - activePrintingArea.width) / 2,
          y: activePrintingArea.offsetTop,
          width: activePrintingArea.width,
          height: activePrintingArea.height,
        },
        readOnlyCustomizations: this.readOnlyCustomizations,
        activeCustomization: this.customization,
        screenPrintingColor: this.color,
        color: this.color,
        group: this.group,
      };

      if (this.zone.type === ZoneType.TEXT) {
        const { typoId, bold } = this.textCustomization;

        const typo =
          (typoId &&
            this.$store.getters[
              prefixGlassesType(GlassesGetter.FIND_TYPO_BY_ID)
            ](typoId)) ||
          undefined;

        Object.assign(
          props,
          filterProps(this.textCustomization, ['text', 'underlined', 'italic'])
        );

        if (!this.color) {
          props.color = this.textCustomization.color;
        }

        if (typo) {
          props.fontFamily = getTypoName(typo, bold);
        }
      } else if (this.zone.type === ZoneType.IMAGE) {
        props.imageUrl = this.customization.url;
      } else {
        throw new Error('Unknown zone type');
      }

      return props;
    },
    zone() {
      return this.group.zoneMap[this.customization.templateZoneId];
    },
    isFormValid() {
      if (this.zone.type === ZoneType.TEXT) {
        const text = this.textCustomization?.text;
        const typoId = this.textCustomization?.typoId;
        return !!text && !!typoId;
      }

      return true;
    },
    isDebugAiGenEnabled() {
      return new URLSearchParams(globalThis.location.search).has('debugAiGen');
    },
  },
  watch: {
    customization: {
      handler(customization) {
        this.customizationCustomConfig =
          (customization &&
            ZoneCustomizationCustomConfigProps.every(
              prop => prop in customization
            ) &&
            filterProps(customization, ZoneCustomizationCustomConfigProps)) ||
          undefined;

        if (this.zone.type === ZoneType.TEXT) {
          this.textCustomization = {
            text: customization.text || undefined,
            bold: customization.bold || false,
            italic: customization.italic || false,
            underlined: customization.underlined || false,
            typoId: customization.typoId || undefined,
            color: customization.color || undefined,
          };
        }
      },
      immediate: true,
    },
  },
  methods: {
    focusTextInput() {
      this.$refs.textToolbar?.focusTextInput();
    },
    goBack() {
      this.$emit('go-back');
    },
    goToAiEditing() {
      this.$emit('go-to-ai-editing');
    },
    onSubmit(e) {
      e.preventDefault();

      const customization = {
        // Inherit customization
        ...this.customization,
        // Extend customization with user updates
        ...this.customizationCustomConfig,
      };

      if (this.zone.type === ZoneType.TEXT) {
        Object.assign(customization, this.textCustomization);
      } else if (this.zone.type === ZoneType.IMAGE) {
        Object.assign(customization, this.customizationCustomConfig);
      } else {
        throw new Error('Unknown zone type.');
      }

      this.$emit('submit', customization);
    },
    resetText() {
      this.textCustomization = {
        text: undefined,
        bold: false,
        underlined: false,
        italic: false,
        typoId: undefined,
        color: undefined,
      };

      this.$emit('reset-text');
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-layer-editor-modal-edit {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  &__body,
  &__actions {
    display: flex; // Break margin fusion
    flex-direction: column;
  }

  &__actions {
    background: $color-white;
  }

  &__body {
    flex: 1;
    overflow-y: scroll;
    justify-content: center;
  }

  &__actions-container {
    margin: 1em;
  }

  &__actions-toolbar {
    margin-bottom: 0;
  }

  &__stage {
    aspect-ratio: 1.1;
  }

  &__sub-header {
    background-color: $color-white;
  }

  &__btn-next {
    padding-left: 0.75rem;
    margin-left: 0.625rem;
    height: 2.75rem;

    &::after {
      @extend %icon;
      @extend %icon--arrow-button;

      font-size: 1.125rem;
    }

    @include breakpoint($breakpoint-l) {
      padding-left: 0.9375rem;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}
</style>
